import { api } from '@/lib/api/client';

export interface CustomerListItem {
  id: string;
  customerId: string;
  customerName: string | null;
  name: string | null;
  phoneNumber: string;
  whatsappNumber: string | null;
  email: string | null;
  tags: string[];
  totalOrders: number;
  totalInteractions: number;
  lifetimeValue: number;
  totalConversations: number;
  escalationCount: number;
  satisfactionScore: number | null;
  lastContactDate: string | null;
  firstContactDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerDetail extends CustomerListItem {
  storeId: string;
  avgOrderValue: number | null;
  lastOrderDate: string | null;
  contactMethod: string | null;
  preferences: Record<string, unknown>;
  notes: string | null;
  customerMetadata: Record<string, unknown>;
  attributes: Record<string, unknown>;
}

export interface UpdateCustomerData {
  customer_name?: string;
  email?: string;
  notes?: string;
  tags?: string[];
}

// Map snake_case API response to camelCase frontend interface
function mapCustomer(raw: Record<string, unknown>): CustomerListItem {
  return {
    id: raw.id as string,
    customerId: raw.customer_id as string,
    customerName: raw.customer_name as string | null,
    name: raw.name as string | null,
    phoneNumber: (raw.phone_number as string) || '',
    whatsappNumber: raw.whatsapp_number as string | null,
    email: raw.email as string | null,
    tags: (raw.tags as string[]) || [],
    totalOrders: (raw.total_orders as number) || 0,
    totalInteractions: (raw.total_interactions as number) || 0,
    lifetimeValue: Number(raw.lifetime_value) || 0,
    totalConversations: (raw.total_conversations as number) || 0,
    escalationCount: (raw.escalation_count as number) || 0,
    satisfactionScore: raw.satisfaction_score != null ? Number(raw.satisfaction_score) : null,
    lastContactDate: raw.last_contact_date as string | null,
    firstContactDate: raw.first_contact_date as string | null,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  };
}

function mapCustomerDetail(raw: Record<string, unknown>): CustomerDetail {
  return {
    ...mapCustomer(raw),
    storeId: raw.store_id as string,
    avgOrderValue: raw.avg_order_value != null ? Number(raw.avg_order_value) : null,
    lastOrderDate: raw.last_order_date as string | null,
    contactMethod: raw.contact_method as string | null,
    preferences: (raw.preferences as Record<string, unknown>) || {},
    notes: raw.notes as string | null,
    customerMetadata: (raw.customer_metadata as Record<string, unknown>) || {},
    attributes: (raw.attributes as Record<string, unknown>) || {},
  };
}

export interface ProductVariantResult {
  id: string;
  title: string;
  sku: string | null;
  price: string;
  inventoryQuantity: number;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  partNumber: string | null;
}

export interface ProductSearchResult {
  id: string;
  title: string;
  vendor: string | null;
  productType: string | null;
  partNumber: string | null;
  image: string | null;
  available: boolean;
  variants: ProductVariantResult[];
}

export interface CustomerOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  status: string;
  items: CustomerOrderItem[];
  total: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerOrderItem {
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  quantity: number;
  price: number;
  total: number;
}

function mapOrder(raw: Record<string, unknown>): CustomerOrder {
  const items = (raw.items as Record<string, unknown>[]) || [];
  return {
    id: raw.id as string,
    orderNumber: raw.order_number as string,
    customerId: raw.customer_id as string,
    status: raw.status as string,
    items: items.map((item) => ({
      productId: (item.product_id as string) || '',
      productName: (item.name as string) || (item.title as string) || '',
      variantId: item.variant_id as string | undefined,
      variantName: item.variant_title as string | undefined,
      quantity: (item.quantity as number) || 0,
      price: Number(item.price) || 0,
      total: Number(item.total) || 0,
    })),
    total: Number(raw.total_amount) || 0,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  };
}

export interface ShippingAddress {
  first_name: string;
  last_name: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  country: string;
  zip: string;
  phone?: string;
}

export interface OrderDetail extends CustomerOrder {
  paymentStatus: string;
  fulfillmentStatus: string;
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  currency: string;
  shippingAddress: ShippingAddress | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  notes: string | null;
  tags: string[];
}

function mapOrderDetail(raw: Record<string, unknown>): OrderDetail {
  const base = mapOrder(raw);
  return {
    ...base,
    paymentStatus: (raw.payment_status as string) || '',
    fulfillmentStatus: (raw.fulfillment_status as string) || '',
    subtotal: Number(raw.subtotal) || 0,
    tax: Number(raw.tax) || 0,
    shipping: Number(raw.shipping) || 0,
    discount: Number(raw.discount) || 0,
    currency: (raw.currency as string) || 'USD',
    shippingAddress: (raw.shipping_address as ShippingAddress) || null,
    trackingNumber: (raw.tracking_number as string) || null,
    trackingUrl: (raw.tracking_url as string) || null,
    notes: (raw.notes as string) || null,
    tags: (raw.tags as string[]) || [],
  };
}

export interface CustomerNote {
  id: string;
  customerId: string;
  staffId: string;
  staffName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

function mapNote(raw: Record<string, unknown>): CustomerNote {
  return {
    id: raw.id as string,
    customerId: raw.customer_id as string,
    staffId: raw.staff_id as string,
    staffName: raw.staff_name as string,
    content: raw.content as string,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  };
}

export interface DraftOrderResult {
  draft_id: string;
  name: string;
  customer_id: string;
  status: string;
  total: string;
  currency: string;
  invoice_url: string | null;
}

export const customerService = {
  async listCustomers(params?: {
    skip?: number;
    limit?: number;
    phone_number?: string;
    tag?: string;
  }): Promise<CustomerListItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.skip) queryParams.set('skip', String(params.skip));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.phone_number) queryParams.set('phone_number', params.phone_number);
    if (params?.tag) queryParams.set('tag', params.tag);
    const qs = queryParams.toString();
    const url = `/customers${qs ? `?${qs}` : ''}`;
    const data = await api.get<Record<string, unknown>[]>(url);
    return data.map(mapCustomer);
  },

  async getCustomer(id: string): Promise<CustomerDetail> {
    const data = await api.get<Record<string, unknown>>(`/customers/${encodeURIComponent(id)}`);
    return mapCustomerDetail(data);
  },

  async updateCustomer(id: string, data: UpdateCustomerData): Promise<CustomerDetail> {
    const resp = await api.patch<Record<string, unknown>>(`/customers/${encodeURIComponent(id)}`, data);
    return mapCustomerDetail(resp);
  },

  async getCustomerOrders(id: string): Promise<CustomerOrder[]> {
    const data = await api.get<Record<string, unknown>[]>(`/customers/${encodeURIComponent(id)}/orders`);
    return data.map(mapOrder);
  },

  async getOrder(customerId: string, orderId: string): Promise<OrderDetail> {
    const data = await api.get<Record<string, unknown>>(
      `/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`
    );
    return mapOrderDetail(data);
  },

  async getCustomerNotes(id: string): Promise<CustomerNote[]> {
    const data = await api.get<Record<string, unknown>[]>(`/customers/${encodeURIComponent(id)}/notes`);
    return data.map(mapNote);
  },

  async addNote(customerId: string, content: string): Promise<CustomerNote> {
    const data = await api.post<Record<string, unknown>>(`/customers/${encodeURIComponent(customerId)}/notes`, { content });
    return mapNote(data);
  },

  async createOrder(
    customerId: string,
    lineItems: { variant_id: string; quantity: number }[],
    note?: string
  ): Promise<DraftOrderResult> {
    return api.post<DraftOrderResult>(`/customers/${encodeURIComponent(customerId)}/orders`, {
      line_items: lineItems,
      note,
    });
  },

  async searchProducts(query: string): Promise<ProductSearchResult[]> {
    return api.get<ProductSearchResult[]>(`/products/search?q=${encodeURIComponent(query)}`);
  },
};
